import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import useNavProject from '../../hooks/useNavProject';
import useUserContext from '../../hooks/useUserContext';
import { NavSubItem } from './NavBar';
import './NavBar.less';
import {
 getProjectPerms, isAdmin, isModerator, isStratAdmin, isStratCurator,
} from '../../services/rolesSvc';
import { getPortfolioBudgetLinkVisibility, getProjectReportLinkVisibility, getStratReportLinkVisibility } from './NavItemProjectHelper';
import useAPI from '../../hooks/useAPI';

const defaultYear = Number(DEFAULT_YEAR_WEBPACK_REPORT);
export function NavItemTech() {
  const [tech, setTech] = useState({});

  const techMatch = useRouteMatch('/techs/:stratId');
  const projMatch = useRouteMatch('/techs/:stratId/projs/:projId');
  const projReportMatch = useRouteMatch('/techs/:stratId/projs/:projId/report');
  const techProjMilestonesEditMatch = useRouteMatch('/techs/:stratId/projs/:projId/report/milestone/:milestoneId/edit');
  const portfolioMatch = useRouteMatch('/techs/:stratId/portfolios/:portfolioId');
  const portfReportTypeMatch = useRouteMatch('/techs/:stratId/portfolios/:portfolioId/report');
  const portfProjMatch = useRouteMatch('/techs/:stratId/portfolios/:portfolioId/projs/:projId');
  const portfProjReportTypeMatch = useRouteMatch('/techs/:stratId/portfolios/:portfolioId/projs/:projId/report/:type');
  const portfProjReportMatch = useRouteMatch('/techs/:stratId/portfolios/:portfolioId/projs/:projId/report');
  const portfArticleReportYearMatch = useRouteMatch('/techs/:stratId/portfolios/:portfolioId/projs/:projId/report/year/:reportYear');

  // Добавляем маршруты для архивных проектов
  const stratsMatch = useRouteMatch('/strats/:stratId');
  const stratReportMatch = useRouteMatch('/strats/:stratId/report');
  const stratsProjMatch = useRouteMatch('/strats/:stratId/projs/:projId');
  const stratsProjReportMatch = useRouteMatch('/strats/:stratId/projs/:projId/report');
  const stratsPortfolioMatch = useRouteMatch('/strats/:stratId/portfolios/:portfolioId');
  const stratsPortfReportTypeMatch = useRouteMatch('/strats/:stratId/portfolios/:portfolioId/report');
  const stratsPortfProjMatch = useRouteMatch('/strats/:stratId/portfolios/:portfolioId/projs/:projId');
  const stratsPortfProjReportTypeMatch = useRouteMatch('/strats/:stratId/portfolios/:portfolioId/projs/:projId/report/:type');
  const stratsPortfProjReportMatch = useRouteMatch('/strats/:stratId/portfolios/:portfolioId/projs/:projId/report');

  const api = useAPI();
  const history = useHistory();
  const userCtx = useUserContext();
  const navProject = useNavProject();

  const reportYear = useMemo(() => {
    if (!portfArticleReportYearMatch) return defaultYear;
    return !isNaN(portfArticleReportYearMatch.params.reportYear)
      ? portfArticleReportYearMatch.params.reportYear
      : defaultYear;
  }, [portfArticleReportYearMatch]);

  const techId = useMemo(() => {
    if (!techMatch) return null;
    return techMatch.params.stratId;
  }, [techMatch]);

  const portfolioId = useMemo(() => {
    if (!portfolioMatch) return null;
    return portfolioMatch.params.portfolioId;
  }, [portfolioMatch]);

  useEffect(() => {
    const getType = async () => {
      const tech = await api.get(`/mng/strategies/${techId}`);
      const type = tech.type;
      const path = history.location.pathname;
      if (path.includes('techs') && type === 'strategy') {
        history.replace(path.replace('techs', 'strats'));
      }
      setTech(tech);
    };
    if (techId) {
      getType();
    }
  }, [techId]);

  useEffect(() => {
    if (!techId && navProject.techTitle) {
      navProject.setTechTitle(null);
    }
  }, [techId]);

  const projId = useMemo(() => {
    if (!projMatch) return null;
    return projMatch.params.projId;
  }, [projMatch]);

  const portfProjId = useMemo(() => {
    if (!portfProjMatch) return null;
    return portfProjMatch.params.projId;
  }, [portfProjMatch]);

  useEffect(() => {
    if (!projId && navProject.projTitle) {
      navProject.setProjTitle(null);
    }
  }, [projId]);

  const showProjReportLink = useMemo(() => {
    if (!userCtx || !techId) return false;
    const projPerms = getProjectPerms(userCtx, projId, techId, 0, 0);
    return projId !== 'new' && (projPerms.canWrite || projPerms.canRead);
  }, [userCtx, projId, techId]);

  const showProjMembersLink = useMemo(() => {
    if (!userCtx || !techId) return false;
    return techId !== 'new';
  }, [userCtx, techId, navProject]);

  const showPortfolioSubLink = useMemo(() => {
    if (!userCtx || !techId || !portfolioId) return false;
    const portfolioPerms = getPortfolioBudgetLinkVisibility(userCtx, techId, portfolioId);
    return portfolioId !== 'new' && portfolioPerms;
  }, [userCtx, techId, portfolioId]);

  const showPortfolioProjReportLink = useMemo(() => {
    if (!userCtx || !portfProjId || !portfolioId || !techId) return false;
    const projPerms = getProjectPerms(userCtx, portfProjId, techId, portfolioId);
    return portfProjId !== 'new' && (projPerms.canRead || projPerms.canWrite);
  }, [userCtx, portfProjId, techId]);

  const milestoneId = useMemo(() => techProjMilestonesEditMatch?.params?.milestoneId, [techProjMilestonesEditMatch]);

  const showTechBudgetLink = useMemo(() => isAdmin(userCtx) || isModerator(userCtx) || isStratAdmin(userCtx, 4) || isStratCurator(userCtx, 4), [userCtx, techId]);

  // Добавляем обработку архивных проектов
  const stratsId = useMemo(() => {
    if (!stratsMatch) return null;
    return stratsMatch.params.stratId;
  }, [stratsMatch]);

  const stratsProjId = useMemo(() => {
    if (!stratsProjMatch) return null;
    return stratsProjMatch.params.projId;
  }, [stratsProjMatch]);

  const stratsPortfolioId = useMemo(() => {
    if (!stratsPortfolioMatch) return null;
    return stratsPortfolioMatch.params.portfolioId;
  }, [stratsPortfolioMatch]);

  const stratsPortfProjId = useMemo(() => {
    if (!stratsPortfProjMatch) return null;
    return stratsPortfProjMatch.params.projId;
  }, [stratsPortfProjMatch]);

  const showStratReportLink = useMemo(() => {
    if (!userCtx || !stratsId) return false;
    return getStratReportLinkVisibility(userCtx, stratsId);
  }, [userCtx, stratsId]);

  const showStratProjReportLink = useMemo(() => getProjectReportLinkVisibility(userCtx, stratsId, stratsPortfolioId, stratsProjId), [userCtx, stratsId, stratsProjId, stratsPortfolioId]);

  const showStratPortfolioSubLink = useMemo(() => {
    if (!userCtx || !stratsId || !stratsPortfolioId) return false;
    const portfolioPerms = getPortfolioBudgetLinkVisibility(userCtx, stratsId, stratsPortfolioId);
    return stratsPortfolioId !== 'new' && portfolioPerms;
  }, [userCtx, stratsId, stratsPortfolioId]);

  const showStratPortfolioProjReportLink = useMemo(() => {
    if (!userCtx || !stratsPortfProjId || !stratsPortfolioId || !stratsId) return false;
    const projPerms = getProjectPerms(userCtx, stratsPortfProjId, stratsId, stratsPortfolioId);
    return stratsPortfProjId !== 'new' && (projPerms.canRead || projPerms.canWrite);
  }, [userCtx, stratsPortfProjId, stratsId, stratsPortfolioId]);

  return (
    <>
      {techId && navProject.techTitle && (
      <>
        <NavSubItem style={{ marginLeft: 8 }} inactive={!projMatch} path={`/techs/${techId}`} exact>{navProject.techTitle}</NavSubItem>
        {!projMatch && !portfolioMatch
            && (
            <>
              <NavSubItem style={{ marginLeft: 16 }} path={`/techs/${techId}`} exact>Цифровой паспорт СТП</NavSubItem>
              {showProjMembersLink && showProjReportLink && <NavSubItem style={{ marginLeft: 16 }} path={`/techs/${techId}/report`}>Отчет СТП</NavSubItem>}
              {(showProjReportLink || showTechBudgetLink) && (tech.hasProjects || tech.hasContracts) && <NavSubItem style={{ marginLeft: 16 }} path={`/techs/${techId}/budget`}>Управление бюджетом</NavSubItem>}
              {showProjMembersLink && <NavSubItem style={{ marginLeft: 16 }} path={`/techs/${techId}/members`}>Участники СТП</NavSubItem>}
            </>
            )}
      </>
      )}
      {portfolioId && navProject.portfolioTitle && (
        <>
          <NavSubItem style={{ marginLeft: 16 }} inactive={!portfProjMatch} path={`/techs/${techId}/portfolios/${portfolioId}`} exact>
            {`${portfolioId !== 'new' ? 'Портфель проектов: ' : ''}${navProject.portfolioTitle}`}
          </NavSubItem>
          {!portfProjMatch && (
          <>
            <NavSubItem style={{ marginLeft: 24 }} path={`/techs/${techId}/portfolios/${portfolioId}`} exact>Цифровой паспорт</NavSubItem>
            {showPortfolioSubLink && <NavSubItem style={{ marginLeft: 24 }} path={`/techs/${techId}/portfolios/${portfolioId}/report`} exact>Отчет</NavSubItem>}
            {showPortfolioSubLink && (
            <>
              {portfReportTypeMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/report/reports`}>Реестр отчетов</NavSubItem>}
              {portfReportTypeMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/report/articles`}>Статьи</NavSubItem>}
              {portfReportTypeMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/report/incomes`}>Доходы</NavSubItem>}
              {portfReportTypeMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/report/requests`}>Заявки</NavSubItem>}
              {portfReportTypeMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/report/unified`}>Унифицированные показатели</NavSubItem>}
              {portfReportTypeMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/report/milestones`}>Вехи портфеля</NavSubItem>}
              {portfReportTypeMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/report/receipts`}>Поступления</NavSubItem>}
            </>
            )}
            {showPortfolioSubLink && <NavSubItem style={{ marginLeft: 24 }} path={`/techs/${techId}/portfolios/${portfolioId}/budget`}>Управление бюджетом</NavSubItem>}
            {showPortfolioSubLink && <NavSubItem style={{ marginLeft: 24 }} path={`/techs/${techId}/portfolios/${portfolioId}/members`}>Участники</NavSubItem>}
          </>
          )}
        </>
      )}
      {projId && navProject.projTitle && (
      <>
        <NavSubItem style={{ marginLeft: 16 }} inactive>{navProject.projTitle}</NavSubItem>
        <>
          <NavSubItem style={{ marginLeft: 24 }} path={`/techs/${techId}/projs/${projId}`} exact>Цифровой паспорт</NavSubItem>
          {showProjReportLink && <NavSubItem style={{ marginLeft: 24 }} path={`/techs/${techId}/projs/${projId}/report`}>Отчет</NavSubItem>}
          {showProjReportLink && projReportMatch && (
            <>
              <NavSubItem style={{ marginLeft: 32 }} path={`/techs/${techId}/projs/${projId}/report/year/${defaultYear}`}>Ежегодный отчёт</NavSubItem>
              <NavSubItem style={{ marginLeft: 32 }} path={`/techs/${techId}/projs/${projId}/report/milestones`}>Вехи проекта</NavSubItem>
            </>
          )}
          {showProjReportLink && <NavSubItem style={{ marginLeft: 24 }} path={`/techs/${techId}/projs/${projId}/budget`}>Управление бюджетом</NavSubItem>}
          {showProjReportLink && <NavSubItem style={{ marginLeft: 24 }} path={`/techs/${techId}/projs/${projId}/members`}>Участники</NavSubItem>}
        </>
      </>
      )}
      {portfProjId && navProject.portfProjTitle && (
        <>
          <NavSubItem style={{ marginLeft: 16 }} inactive>{navProject.portfProjTitle}</NavSubItem>
          <>
            <NavSubItem style={{ marginLeft: 24 }} path={`/techs/${techId}/portfolios/${portfolioId}/projs/${portfProjId}`} exact>Цифровой паспорт</NavSubItem>
            {showPortfolioProjReportLink && <NavSubItem inactive={portfProjReportMatch} style={{ marginLeft: 24 }} path={`/techs/${techId}/portfolios/${portfolioId}/projs/${portfProjId}/report`}>Отчет</NavSubItem>}
            {showPortfolioProjReportLink && (
              <>
                {portfProjReportMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/projs/${portfProjId}/yearReport/year/${reportYear}`}>Ежегодный отчет</NavSubItem>}
                {portfProjReportMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/projs/${portfProjId}/report/articles`}>Статьи</NavSubItem>}
                {portfProjReportMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/projs/${portfProjId}/report/income`}>Доходы</NavSubItem>}
                {portfProjReportMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/projs/${portfProjId}/report/requests`} exact>Заявки</NavSubItem>}
                {portfProjReportMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/projs/${portfProjId}/report/unified`} exact>Унифицированные показатели</NavSubItem>}
                {portfProjReportMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/projs/${portfProjId}/report/milestones`} exact>Вехи проекта</NavSubItem>}
                {portfProjReportMatch && <NavSubItem style={{ marginLeft: 36 }} path={`/techs/${techId}/portfolios/${portfolioId}/projs/${portfProjId}/report/receipts`} exact>Поступления</NavSubItem>}
              </>
            )}
            <NavSubItem style={{ marginLeft: 24 }} path={`/techs/${techId}/portfolios/${portfolioId}/projs/${portfProjId}/budget`}>Управление бюджетом</NavSubItem>
            {showPortfolioProjReportLink && <NavSubItem style={{ marginLeft: 24 }} path={`/techs/${techId}/portfolios/${portfolioId}/projs/${portfProjId}/members`}>Участники</NavSubItem>}
          </>
        </>
        )}
      <NavSubItem style={{ marginLeft: 8 }} path="/strats" exact>Стратегические проекты (Архив до 2024)</NavSubItem>

      {(stratsMatch || stratsId) && (
        <>
          {stratsId && navProject.stratTitle && (
            <>
              <NavSubItem style={{ marginLeft: 16 }} inactive={!stratsProjMatch && !stratsPortfolioMatch} path={`/strats/${stratsId}`} exact>{navProject.stratTitle}</NavSubItem>
              {!stratsProjMatch && !stratsPortfolioMatch && (
                <>
                  <NavSubItem style={{ marginLeft: 24 }} path={`/strats/${stratsId}`} exact>Цифровой паспорт СП</NavSubItem>
                  {showStratReportLink && <NavSubItem style={{ marginLeft: 24 }} path={`/strats/${stratsId}/report`}>Отчет СП</NavSubItem>}
                  {showStratReportLink && (
                    <>
                      {stratReportMatch && <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/report/reports`}>Реестр отчетов</NavSubItem>}
                      {stratReportMatch && <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/report/unified`}>Унифицированные показатели проекта СП</NavSubItem>}
                      {stratReportMatch && <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/report/milestones`}>Вехи проектов СП</NavSubItem>}
                      {stratReportMatch && <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/report/receipts`}>Поступления</NavSubItem>}
                    </>
                  )}
                  <NavSubItem style={{ marginLeft: 24 }} path={`/strats/${stratsId}/budget`}>Управление бюджетом</NavSubItem>
                  {showStratReportLink && <NavSubItem style={{ marginLeft: 24 }} path={`/strats/${stratsId}/members`}>Участники СП</NavSubItem>}
                </>
              )}
            </>
          )}

          {stratsProjId && navProject.projTitle && (
            <>
              <NavSubItem style={{ marginLeft: 24 }} inactive>{navProject.projTitle}</NavSubItem>
              <>
                <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/projs/${stratsProjId}`} exact>Цифровой паспорт</NavSubItem>
                {showStratProjReportLink && <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/projs/${stratsProjId}/report`} exact>Отчет</NavSubItem>}
                {showStratProjReportLink && stratsProjReportMatch && (
                  <>
                    <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/projs/${stratsProjId}/report/year/${reportYear}`} exact>Ежегодный отчет</NavSubItem>
                    <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/projs/${stratsProjId}/report/milestones`}>Вехи проекта</NavSubItem>
                    <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/projs/${stratsProjId}/report/receipts`} exact>Поступления</NavSubItem>
                    <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/projs/${stratsProjId}/report/unified`} exact>Унифицированные показатели</NavSubItem>
                  </>
                )}
                <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/projs/${stratsProjId}/budget`}>Управление бюджетом</NavSubItem>
                <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/projs/${stratsProjId}/members`}>Участники</NavSubItem>
              </>
            </>
          )}

          {stratsPortfolioId && navProject.portfolioTitle && (
            <>
              <NavSubItem style={{ marginLeft: 24 }} inactive={!stratsPortfProjMatch} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}`} exact>
                {`${stratsPortfolioId !== 'new' ? 'Портфель проектов: ' : ''}${navProject.portfolioTitle}`}
              </NavSubItem>
              {!stratsPortfProjMatch && (
                <>
                  <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}`} exact>Цифровой паспорт</NavSubItem>
                  {showStratPortfolioSubLink && <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/report`} exact>Отчет</NavSubItem>}
                  {showStratPortfolioSubLink && (
                    <>
                      {stratsPortfReportTypeMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/report/reports`}>Реестр отчетов</NavSubItem>}
                      {stratsPortfReportTypeMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/report/articles`}>Статьи</NavSubItem>}
                      {stratsPortfReportTypeMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/report/incomes`}>Доходы</NavSubItem>}
                      {stratsPortfReportTypeMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/report/requests`}>Заявки</NavSubItem>}
                      {stratsPortfReportTypeMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/report/unified`}>Унифицированные показатели</NavSubItem>}
                      {stratsPortfReportTypeMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/report/milestones`}>Вехи портфеля</NavSubItem>}
                      {stratsPortfReportTypeMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/report/receipts`}>Поступления</NavSubItem>}
                    </>
                  )}
                  {showStratPortfolioSubLink && <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/budget`}>Управление бюджетом</NavSubItem>}
                  {showStratPortfolioSubLink && <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/members`}>Участники</NavSubItem>}
                </>
              )}
            </>
          )}

          {stratsPortfProjId && navProject.portfProjTitle && (
            <>
              <NavSubItem style={{ marginLeft: 24 }} inactive>{navProject.portfProjTitle}</NavSubItem>
              <>
                <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/projs/${stratsPortfProjId}`} exact>Цифровой паспорт</NavSubItem>
                {showStratPortfolioProjReportLink && <NavSubItem inactive={stratsPortfProjReportMatch} style={{ marginLeft: 32 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/projs/${stratsPortfProjId}/report`}>Отчет</NavSubItem>}
                {showStratPortfolioProjReportLink && (
                  <>
                    {stratsPortfProjReportMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/projs/${stratsPortfProjId}/yearReport/year/${reportYear}`}>Ежегодный отчет</NavSubItem>}
                    {stratsPortfProjReportMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/projs/${stratsPortfProjId}/report/articles`}>Статьи</NavSubItem>}
                    {stratsPortfProjReportMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/projs/${stratsPortfProjId}/report/income`}>Доходы</NavSubItem>}
                    {stratsPortfProjReportMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/projs/${stratsPortfProjId}/report/requests`} exact>Заявки</NavSubItem>}
                    {stratsPortfProjReportMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/projs/${stratsPortfProjId}/report/unified`} exact>Унифицированные показатели</NavSubItem>}
                    {stratsPortfProjReportMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/projs/${stratsPortfProjId}/report/milestones`} exact>Вехи проекта</NavSubItem>}
                    {stratsPortfProjReportMatch && <NavSubItem style={{ marginLeft: 40 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/projs/${stratsPortfProjId}/report/receipts`} exact>Поступления</NavSubItem>}
                  </>
                )}
                <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/projs/${stratsPortfProjId}/budget`}>Управление бюджетом</NavSubItem>
                {showStratPortfolioProjReportLink && <NavSubItem style={{ marginLeft: 32 }} path={`/strats/${stratsId}/portfolios/${stratsPortfolioId}/projs/${stratsPortfProjId}/members`}>Участники</NavSubItem>}
              </>
            </>
          )}
        </>
      )}
    </>
  );
}

const styledSubtitle = {
  fontSize: 12, paddingRight: 8, marginTop: 8, marginBottom: 8,
};
